import { Plus } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import {
  useAmendExpense,
  useDropExpense,
  useRecordExpense,
} from '@/features/cases/api/useCase'
import {
  ExpenseFormSheet,
  type ExpenseCarry,
} from '@/features/cases/components/CaseRecordSheets'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { reveal } from '@/features/cases/lib/reveal'
import type { CaseDetail, ExpenseLine } from '@/types/domain'

import { BalanceCell, BalanceTiles } from './BalanceCells'
import { CaseLegPaymentDrawer, type LegPaymentState } from './CaseLegPaymentDrawer'
import { ExpenseTable } from './ExpenseTable'
import { MoneyNotices } from './MoneyNotices'
import { MoneyUnavailable } from './MoneyUnavailable'
import { SupplierPayments } from './SupplierPayments'
import { awaitingPlans, editing, type EditTarget, type ExpenseLink } from './money'

/** What the expense sheet is open on: nothing, a new line, an existing line,
 *  or a new actual entered against a plan that awaits it. */
type ExpenseTarget = EditTarget<ExpenseLine> | ExpenseLink

function isLink(target: ExpenseTarget): target is ExpenseLink {
  return target !== null && target !== 'new' && 'kind' in target
}

/* «Затраты» (S-11 revision, direction A «Реестр»), top to bottom:
 *
 *   1. four tiles — «План» (`plannedCost`), «Факт» (`actualToDate`),
 *      «Оплачено» and «Остаток поставщикам» (`suppliers.totals`, the last in
 *      the attention tone while above zero), every figure served (AD-9);
 *   2. the S-12 notices — «Разнести» brings «Платежи поставщикам» into view;
 *   3. «Строки затрат»: the plan against the fact, line by line, with the leg
 *      drawer a row's «Плечо N» opens (PR #104), unchanged;
 *   4. «Платежи поставщикам»: the outgoing money grouped by supplier.
 *
 * What the client is billed and has paid is «Счета клиенту»'s
 * (`BillingSection`), and the profit is a card on «Обзор». The balances row
 * that used to close this card is retired: its receivable is the billing
 * tab's «Долг клиента», its payables «Остаток поставщикам» here, and its
 * «На согласовании» was never served live. */

/** The costs tab — or, when there is no block, the reason there is none.
 *
 *  THREE STATES, which used to be one. The server serves the block whenever it
 *  can compute it and names its reason when it cannot (see
 *  `cmd/asa/readmodel_case.go`), so this section renders:
 *
 *    · the block, whatever its figures;
 *    · "nothing recorded yet" with the action that records something, for a
 *      case nobody has priced — every FR-70 direct creation and all 241
 *      migrated cases start here (UX-DR-17);
 *    · a failure, with a retry, when the read or the FX-rate lookup behind it
 *      failed (UX-DR-18).
 *
 *  What it no longer renders is a permission refusal. AD-5 gives a deal's money
 *  to the deal's own team, and a caller who cannot reach the case gets a 404 on
 *  the case itself. The old copy said "Money is not shown for your role" to a
 *  founder looking at a brand-new shipment, and the two translated keys behind
 *  it are deleted. The one refusal that does exist — a custom role whose
 *  `case-money` is narrower than its `cases`, served as `moneyUnavailable:
 *  "forbidden"` (story B3, fix round 1) — renders as NOTHING, not as a
 *  sentence: the section is not offered for that case at all.
 *
 *  Takes the RECORD rather than the block, like every other section on this
 *  page: it needs the block, the unavailability reason and the case id, and
 *  three props that must agree with each other are one prop. */
export function MoneySection({
  record,
  onRetry,
}: {
  record: CaseDetail
  /** Refetches the case, which is where the money block comes from. The section
   *  has no query of its own — the block rides the composed detail — so the
   *  retry has to be the page's. */
  onRetry?: () => void
}) {
  const { t } = useTranslation()
  const [target, setTarget] = useState<ExpenseTarget>(null)
  /* What the reader had typed on the actual's side when the sheet switched
     between «Без плана» and a plan; seeded into the next draft. */
  const [carried, setCarried] = useState<ExpenseCarry | undefined>()
  /** «Платежи поставщикам» — where «Разнести» (S-12) brings the reader. */
  const paymentsRef = useRef<HTMLDivElement>(null)
  /* The cost line whose «Плечо N» cell was pressed (S-11) — the drawer about
     that leg's carrier money, or the payment sheet it handed off to. One
     value, so the two can never be open together; `CaseLegPaymentDrawer`
     owns what follows from it. */
  const [legPayment, setLegPayment] = useState<LegPaymentState>(null)

  const caseId = record.id
  const money = record.money
  const quoteNumber = record.quoteNumber

  const recordExpense = useRecordExpense(caseId)
  const amendExpense = useAmendExpense(caseId)
  const dropExpense = useDropExpense(caseId)

  const linkedPlan = isLink(target) ? target.plan : undefined
  const editingExpense = isLink(target) ? undefined : editing(target)
  const expensePending =
    recordExpense.isPending || amendExpense.isPending || dropExpense.isPending
  const expenseFailure =
    recordExpense.error ?? amendExpense.error ?? dropExpense.error

  /* Every way into the sheet starts clean. The three mutations outlive the
     sheet, and their `.error` is what the sheet renders — as the banner and,
     since QW-9, under the field it names — so a refusal left standing would
     greet the NEXT sheet, a new empty line included, with «Должно быть больше
     нуля» under a field nobody had touched. */
  const openExpense = (next: ExpenseTarget, carry?: ExpenseCarry) => {
    recordExpense.reset()
    amendExpense.reset()
    dropExpense.reset()
    setCarried(carry)
    setTarget(next)
  }

  /* A decision rather than a failure (story B3, fix round 1): this reader
     reaches the case through `cases` and not its money through `case-money`.
     Nothing renders — a retry would fetch the same refusal — and the page does
     not offer this section for such a case either (`offeredCaseSections`); this is the
     guard for any other way in. After every hook, since hooks cannot return
     early. */
  if (record.moneyUnavailable === 'forbidden') return null

  /* No block, and the server said why — see MoneyUnavailable. */
  if (!money) return <MoneyUnavailable onRetry={onRetry} />

  /* Nothing has been recorded against this case in any money table: no cost
     line, no agreed rate, no payment and no expected tranche. Distinguished
     from a refusal and from a failure, and offering the one action that fills
     it (UX-DR-17). The rate is agreed elsewhere — it is Finance's act on the
     record, not a control on this card — so the empty state names it in prose
     rather than offering a button that would not work. */
  const untouched =
    money.hasRate === false &&
    money.expenses.length === 0 &&
    money.tranches.length === 0

  /* The four tiles. «Факт» is the actuals recorded so far (`actualToDate`),
     not `actualCost`, which forecasts the lines still waiting on a figure.
     «Остаток поставщикам» is the FORECAST-basis figure with vendorless lines
     included — a different number from the block's FR-30 `payables`
     (http-api.md) — and carries the attention tone while above zero: what is
     still owed is what somebody is phoned about. */
  const tiles = (
    <BalanceTiles label={t('cases.costs.tilesLabel')}>
      <BalanceCell label={t('cases.costs.planned')} amount={money.plannedCost} />
      <BalanceCell label={t('cases.costs.actual')} amount={money.actualToDate} />
      <BalanceCell label={t('cases.costs.paid')} amount={money.suppliers.totals.paid} />
      <BalanceCell
        label={t('cases.costs.outstanding')}
        amount={money.suppliers.totals.outstanding}
        tone={Number(money.suppliers.totals.outstanding.amount) > 0 ? 'attention' : undefined}
      />
    </BalanceTiles>
  )

  /* The S-12 notices, under the tiles (direction A). In BOTH branches: an
     unallocated outbound payment can sit on a case with no rate, no cost and
     no tranche, which is exactly the untouched one. */
  const notices =
    money.doubleCount || money.unallocatedOutbound ? (
      <MoneyNotices
        caseId={caseId}
        money={money}
        className="px-0 pt-0"
        onAllocate={() => {
          if (paymentsRef.current) reveal(paymentsRef.current)
        }}
      />
    ) : null

  /* «Платежи поставщикам», wrapped so «Разнести» has somewhere to land:
     focusable for the keyboard and a screen reader, never in the tab order,
     with no ring — the global `:focus-visible` paints a box-shadow. In both
     branches: cash can go out on a case nobody has priced. */
  const supplierPayments = (
    <div
      ref={paymentsRef}
      tabIndex={-1}
      aria-label={t('cases.costs.suppliers.title')}
      className="outline-none focus-visible:shadow-none"
    >
      <SupplierPayments caseId={caseId} legs={record.legs} suppliers={money.suppliers} />
    </div>
  )

  /* Hoisted out of the return so both branches below can render it: the empty
     state offers the same "record a cost" action the header does, and an action
     that opened nothing would be the trap the legs section's empty state
     deliberately avoids. */
  const sheet = (
    <ExpenseFormSheet
      open={target !== null}
      onClose={() => setTarget(null)}
      record={editingExpense}
      /* A new actual may be entered against a plan still awaiting one (S-4.1
         follow-ups). Choosing one re-targets the sheet through openExpense —
         a LINK, not an edit of the plan — so the draft re-seeds under its own
         key with the typed actual carried across, and a refusal from the
         previous shape does not follow. */
      awaitingPlans={awaitingPlans(money.expenses)}
      linkedPlan={linkedPlan}
      carried={carried}
      onLinkPlan={(plan, carry) => openExpense(plan ? { kind: 'link', plan } : 'new', carry)}
      pending={expensePending}
      error={expenseFailure ? refusalMessage(expenseFailure, t) : undefined}
      failure={expenseFailure}
      onSubmit={(values) => {
        /* The sheet stays OPEN while the save is in flight and stays open
           holding the draft if the server refuses, so nothing typed is lost
           (UX-DR-18) — and for money that matters more than elsewhere: the
           server can refuse a currency it has no cached FX rate for (AD-20),
           which is a failure the typist can do nothing about except retry. */
        const done = { onSuccess: () => setTarget(null) }

        /* An existing line is a PUT on its own id, a new one is a POST. There
           is no longer a question of WHICH row to target: the folded line is
           what the endpoints take, and the server decides which of the two AD-4
           rows the draft means. The three-way branch that used to stand here
           had to guess, and got it wrong three ways — a plan acquiring its
           first actual created a second plan, an edit to both figures saved
           one, and the id it chose was sometimes the actual's.

           `editingExpense.id` is the folded `id` both endpoints want: the
           PLAN's id on a planned line, the actual's own on an unplanned one
           (S-4.1). */
        if (editingExpense) {
          amendExpense.mutate({ lineId: editingExpense.id, draft: values }, done)
          return
        }

        /* An actual against an awaiting plan is that plan's first-actual PUT:
           the plan's id, and its plan EXACTLY as served — the description and
           figure come from the line, not from the draft, so nothing on this
           sheet can re-price the plan. The server books the actual against it
           and gives it the plan's leg and truck. */
        if (linkedPlan) {
          amendExpense.mutate(
            {
              lineId: linkedPlan.id,
              draft: {
                ...values,
                description: linkedPlan.description,
                planned: linkedPlan.planned.amount,
                plannedCurrency: linkedPlan.planned.currency,
              },
            },
            done,
          )
          return
        }

        recordExpense.mutate(values, done)
      }}
      /* Deleting a cost line deletes the LINE — the plan and every actual
         against it, in one transaction, which is what the reader who pressed
         Delete on this row asked for. It used to send `actualId ?? id`, so on a
         line with an invoice it removed the invoice and left the plan sitting
         on screen looking undeleted.

         A second invoice against the plan is NOT a refusal here: the delete
         cascades whatever the count, because "which invoice did you mean" has
         no answer to guess at when both are going. What can still refuse is a
         state of the record — the case closed to money, or the line already
         deleted by somebody else — and that reaches the reader through
         `refusalMessage` with the sheet still open. */
      onDelete={
        editingExpense
          ? () =>
              dropExpense.mutate(editingExpense.id, {
                onSuccess: () => setTarget(null),
              })
          : undefined
      }
    />
  )

  if (untouched) {
    /* NOTHING YET, and said as such. This is the state a case is in from
       creation until somebody prices it or records a cost — every FR-70 direct
       creation and all 241 migrated cases — and it used to render as "money is
       not shown for your role", which sent a founder to ask for a permission
       they already had. */
    return (
      <div className="flex min-w-0 flex-col gap-4">
        {tiles}
        {notices}
        <Card>
          <CardHeader>
            <CardTitle>{t('cases.money.title')}</CardTitle>
            <CardSubtitle>
              {quoteNumber
                ? t('cases.money.subtitleFromQuote', { number: quoteNumber })
                : t('cases.money.subtitle')}
            </CardSubtitle>
          </CardHeader>
          <EmptyState
            title={t('cases.money.untouchedTitle')}
            description={t('cases.money.untouchedDescription')}
            action={
              <Button size="sm" iconLeft={Plus} onClick={() => openExpense('new')}>
                {t('cases.money.recordActual')}
              </Button>
            }
          />
        </Card>
        {supplierPayments}
        {sheet}
      </div>
    )
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {tiles}
      {notices}
      <Card>
        <CardHeader
          action={
            <Button
              variant="secondary"
              size="sm"
              iconLeft={Plus}
              onClick={() => openExpense('new')}
            >
              {t('cases.money.recordActual')}
            </Button>
          }
        >
          <CardTitle>{t('cases.money.title')}</CardTitle>
          <CardSubtitle>
            {quoteNumber
              ? t('cases.money.subtitleFromQuote', { number: quoteNumber })
              : t('cases.money.subtitle')}
          </CardSubtitle>
        </CardHeader>

        {money.expenses.length === 0 ? (
          <EmptyState
            title={t('cases.money.expensesEmptyTitle')}
            description={t('cases.money.expensesEmptyDescription')}
            className="py-6"
          />
        ) : (
          /* The route travels with the block: a cost row names the direction it
             paid for by id (FR-67), and the sequence and the plate it renders
             live on the legs. Passed from the RECORD rather than looked up by a
             query of its own, because both are slices of one composed read and
             a second fetch would be a second answer about the same case. */
          <ExpenseTable
            caseId={caseId}
            legs={record.legs}
            money={money}
            pending={expensePending}
            onEdit={(expense) => openExpense(expense)}
            /* The same drop the sheet's own Delete performs, reached from the
               row instead — the line, its plan and every actual against it, in
               one transaction. The row asks before it calls this. */
            onDelete={(expense) => dropExpense.mutate(expense.id)}
            onOpenLeg={(expense, leg) => setLegPayment({ kind: 'leg', expense, legId: leg.id })}
          />
        )}

        {/* A refusal from the row's delete has nowhere else to land: the sheet
            renders `expenseFailure`, and an inline delete happens with the
            sheet closed. Without this the line would simply stay on screen and
            the reader would press again. */}
        {target === null && dropExpense.error ? (
          <p className="text-negative px-[18px] pt-3 text-[12.5px] leading-snug" role="alert">
            {refusalMessage(dropExpense.error, t)}
          </p>
        ) : null}

      </Card>

      {supplierPayments}

      {sheet}

      {/* Portaled, both of them: the drawer is a right-edge overlay and the
          payment sheet is the leg page's own. A successful payment invalidates
          the case, so the figures above refresh behind it. */}
      <CaseLegPaymentDrawer caseId={caseId} state={legPayment} onStateChange={setLegPayment} />
    </div>
  )
}
